import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSupportTickets, useSupportStats } from '@/hooks/use-support-tickets';
import { useAllSupportFAQs, useCreateSupportFAQ, useUpdateSupportFAQ, useDeleteSupportFAQ } from '@/hooks/use-support-faqs';
import { useAllSupportManuals, useDeleteSupportManual } from '@/hooks/use-support-manuals';
import { useSupportCategories } from '@/hooks/use-support-categories';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MoreHorizontal, Search, Plus, TicketIcon, MessageSquare, HelpCircle, BookOpen, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FAQModal } from '@/components/support/admin/FAQModal';
import { ManualModal } from '@/components/support/admin/ManualModal';
import { TicketDetailModal } from '@/components/support/admin/TicketDetailModal';
import { ManualContent } from '@/components/support/ManualContent';
import { htmlToMarkdown, parseMarkdownTOC, TOCItem } from '@/lib/markdown-utils';
import { ManualTOC } from '@/components/support/ManualTOC';
import { ManualABNTInline } from '@/components/support/ManualABNTInline';
import { ManualABNTTOC } from '@/components/support/ManualABNTTOC';
import { fleetoneABNTManual } from '@/data/manuals/fleetone-abnt-content';
import { utmGuideManual } from '@/data/manuals/utm-guide-content';
import { trackingoneManual } from '@/data/manuals/trackingone-content';
import type { ManualABNTData } from '@/data/manuals/fleetone-abnt-content';

export default function SupportPage() {
  const { data: tickets, isLoading: loadingTickets } = useSupportTickets();
  const { data: faqs, isLoading: loadingFAQs } = useAllSupportFAQs();
  const { data: manuals, isLoading: loadingManuals } = useAllSupportManuals();
  const { data: stats } = useSupportStats();
  const [ticketFilter, setTicketFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [manualFilter, setManualFilter] = useState('');
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<any>(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [editingManual, setEditingManual] = useState<any>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [selectedManual, setSelectedManual] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeAnchor, setActiveAnchor] = useState<string>('');
  const manualContentRef = useRef<HTMLDivElement>(null);
  const [selectedManualType, setSelectedManualType] = useState<'standard' | 'abnt' | null>(null);
  const [selectedABNTManual, setSelectedABNTManual] = useState<ManualABNTData | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const deleteFAQ = useDeleteSupportFAQ();
  const deleteManual = useDeleteSupportManual();

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate TOC when standard manual is selected
  useEffect(() => {
    if (selectedManual && selectedManualType === 'standard') {
      const markdown = htmlToMarkdown(selectedManual.content);
      const toc = parseMarkdownTOC(markdown);
      setTocItems(toc);
      setActiveAnchor('');
    } else if (selectedManualType !== 'abnt') {
      setTocItems([]);
      setActiveAnchor('');
    }
  }, [selectedManual, selectedManualType]);

  // Filtered manuals
  const filteredManuals = manuals?.filter(manual => 
    manual.title.toLowerCase().includes(manualFilter.toLowerCase()) ||
    manual.category.toLowerCase().includes(manualFilter.toLowerCase())
  ) || [];

  // Mutation for updating ticket status
  const updateTicketStatus = useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: string }) => {
      const { error } = await supabase
        .from('support_tickets')
        .update({ status })
        .eq('id', ticketId);

      if (error) throw error;
    },
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      toast({
        title: "Status atualizado",
        description: "O status do chamado foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o status do chamado.",
        variant: "destructive",
      });
    },
  });

  const handleUpdateTicketStatus = async (ticketId: string, status: string) => {
    updateTicketStatus.mutate({ ticketId, status });
  };

  const openTicketDetail = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setShowTicketDetail(true);
  };

  const filteredTickets = tickets?.filter(ticket => {
    const matchesFilter = ticketFilter === '' || 
      ticket.title.toLowerCase().includes(ticketFilter.toLowerCase()) ||
      ticket.description.toLowerCase().includes(ticketFilter.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    
    return matchesFilter && matchesStatus;
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'open': return 'destructive';
      case 'in_progress': return 'default';
      case 'resolved': return 'secondary';
      case 'closed': return 'outline';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aberto';
      case 'in_progress': return 'Em Andamento';
      case 'resolved': return 'Resolvido';
      case 'closed': return 'Fechado';
      default: return status;
    }
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return priority;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Central de Suporte</h1>
          <p className="text-muted-foreground">
            Gerencie chamados, FAQs e documentação
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Chamados</CardTitle>
            <TicketIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Abertos</CardTitle>
            <TicketIcon className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats?.open || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
            <TicketIcon className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{stats?.inProgress || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolvidos</CardTitle>
            <TicketIcon className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats?.resolved || 0}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="tickets" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tickets">Chamados</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="manuals">Manuais</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Chamados de Suporte</CardTitle>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar chamados..."
                      className="pl-8 w-64"
                      value={ticketFilter}
                      onChange={(e) => setTicketFilter(e.target.value)}
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os Status</SelectItem>
                      <SelectItem value="open">Abertos</SelectItem>
                      <SelectItem value="in_progress">Em Andamento</SelectItem>
                      <SelectItem value="resolved">Resolvidos</SelectItem>
                      <SelectItem value="closed">Fechados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Sistema</TableHead>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingTickets ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        Carregando chamados...
                      </TableCell>
                    </TableRow>
                  ) : filteredTickets?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <TicketIcon className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">
                            {ticketFilter || statusFilter !== 'all' 
                              ? 'Nenhum chamado encontrado com os filtros aplicados'
                              : 'Nenhum chamado foi criado ainda'
                            }
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets?.map((ticket) => (
                      <TableRow 
                        key={ticket.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openTicketDetail(ticket.id)}
                      >
                        <TableCell className="font-medium">{ticket.title}</TableCell>
                        <TableCell>{((ticket as any).user_profile as any)?.full_name || ((ticket as any).user_profile as any)?.email || '—'}</TableCell>
                        <TableCell>{((ticket as any).company as any)?.name || '—'}</TableCell>
                        <TableCell>{(ticket.category as any)?.name || 'Sem categoria'}</TableCell>
                        <TableCell>
                          {(ticket.system as any)?.name || <span className="text-muted-foreground">Geral</span>}
                        </TableCell>
                        <TableCell>
                          <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                            {getPriorityLabel(ticket.priority)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                         <Badge variant={getStatusBadgeVariant(ticket.status)}>
                           {getStatusLabel(ticket.status)}
                         </Badge>
                       </TableCell>
                        <TableCell>
                          {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                className="h-8 w-8 p-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTicketDetail(ticket.id);
                                }}
                              >
                                Ver Detalhes
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicketStatus(ticket.id, 'in_progress');
                                }}
                              >
                                Marcar como Em Andamento
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicketStatus(ticket.id, 'resolved');
                                }}
                              >
                                Marcar como Resolvido
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUpdateTicketStatus(ticket.id, 'closed');
                                }}
                              >
                                Fechar Chamado
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Gestão de FAQs</CardTitle>
                <Button onClick={() => setShowFAQModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar FAQ
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pergunta</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqs?.map((faq) => (
                    <TableRow key={faq.id}>
                      <TableCell className="font-medium">{faq.question}</TableCell>
                      <TableCell>{faq.support_categories?.name}</TableCell>
                      <TableCell>
                        <Badge variant={faq.is_published ? 'default' : 'secondary'}>
                          {faq.is_published ? 'Publicado' : 'Rascunho'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(faq.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setEditingFAQ(faq);
                                setShowFAQModal(true);
                              }}
                            >
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => deleteFAQ.mutate(faq.id)}
                              className="text-destructive"
                            >
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manuals" className="space-y-4">
          {isMobile ? (
            <Tabs defaultValue="list" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="list">Lista</TabsTrigger>
            <TabsTrigger value="toc" disabled={!selectedManual}>Índice</TabsTrigger>
            <TabsTrigger value="content" disabled={!selectedManual}>
              Conteúdo
            </TabsTrigger>
          </TabsList>

              <TabsContent value="list">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Manuais</CardTitle>
                      <Button 
                        size="sm"
                        onClick={() => {
                          setEditingManual(null);
                          setShowManualModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="pt-2">
                      <Input
                        placeholder="Buscar..."
                        value={manualFilter}
                        onChange={(e) => setManualFilter(e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="p-3">
                    <div className="space-y-2">
                      {filteredManuals.map(manual => (
                        <button
                          key={manual.id}
                          onClick={() => setSelectedManual(manual)}
                          className={cn(
                            "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                            selectedManual?.id === manual.id && "bg-muted ring-1 ring-primary"
                          )}
                        >
                          <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                          <div className="flex gap-2 mt-1.5 flex-wrap">
                            <Badge variant="secondary" className="text-xs">{manual.category}</Badge>
                            {manual.systems && (
                              <Badge variant="outline" className="text-xs">{manual.systems.name}</Badge>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
          </TabsContent>
          
          <TabsContent value="toc">
            {selectedManual && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Índice</CardTitle>
                </CardHeader>
                <CardContent>
                  <ManualTOC
                    items={tocItems}
                    activeAnchor={activeAnchor}
                    onItemClick={(anchor) => {
                      setActiveAnchor(anchor);
                      const tabTrigger = document.querySelector('[value="content"]') as HTMLElement;
                      tabTrigger?.click();
                      setTimeout(() => {
                        const element = document.getElementById(anchor);
                        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="content">
                {selectedManual && (
                  <Card>
                    <CardHeader className="border-b pb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{selectedManual.title}</CardTitle>
                          <CardDescription className="mt-1">
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                              <Badge variant="secondary">{selectedManual.category}</Badge>
                              {selectedManual.systems && (
                                <Badge variant="outline">{selectedManual.systems.name}</Badge>
                              )}
                              {selectedManual.updated_at && (
                                <span className="text-muted-foreground">
                                  • {format(new Date(selectedManual.updated_at), "dd/MM/yyyy")}
                                </span>
                              )}
                            </div>
                          </CardDescription>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditingManual(selectedManual);
                              setShowManualModal(true);
                            }}>
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => deleteManual.mutate(selectedManual.id)}
                              className="text-destructive"
                            >
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <ManualContent 
                        content={htmlToMarkdown(selectedManual.content)} 
                        manualId={selectedManual.id}
                      />
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <div className="grid grid-cols-[280px_200px_1fr] gap-4 h-[calc(100vh-280px)] min-h-0">
              {/* Coluna Esquerda - Lista de Manuais */}
              <Card className="flex flex-col h-full">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Manuais</CardTitle>
                    <Button 
                      size="sm"
                      onClick={() => {
                        setEditingManual(null);
                        setShowManualModal(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                  <div className="pt-2">
                    <Input
                      placeholder="Buscar..."
                      value={manualFilter}
                      onChange={(e) => setManualFilter(e.target.value)}
                      className="h-9"
                    />
                  </div>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-y-auto p-3 custom-scrollbar">
                  <div className="space-y-2">
                    {/* Manual ABNT do FleetONE */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(fleetoneABNTManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === fleetoneABNTManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Manual FleetONE</p>
                          <p className="text-xs text-muted-foreground">Gestão de Frota</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">ABNT</Badge>
                        <Badge variant="outline" className="text-xs">v{fleetoneABNTManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Manual ABNT do Guia UTM */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(utmGuideManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === utmGuideManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Guia UTM</p>
                          <p className="text-xs text-muted-foreground">Rastreamento de Campanhas</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">Marketing</Badge>
                        <Badge variant="outline" className="text-xs">v{utmGuideManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Manual ABNT do TrackingONE */}
                    <button
                      onClick={() => {
                        setSelectedManualType('abnt');
                        setSelectedABNTManual(trackingoneManual);
                        setSelectedManual(null);
                      }}
                      className={cn(
                        "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                        selectedManualType === 'abnt' && selectedABNTManual === trackingoneManual && "bg-muted ring-1 ring-primary"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Manual TrackingONE</p>
                          <p className="text-xs text-muted-foreground">Rastreamento GPS</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1.5">
                        <Badge variant="secondary" className="text-xs">ABNT</Badge>
                        <Badge variant="outline" className="text-xs">v{trackingoneManual.metadata.version}</Badge>
                      </div>
                    </button>

                    {/* Separador */}
                    {filteredManuals.length > 0 && (
                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-background px-2 text-muted-foreground">
                            Outros Manuais
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Manuais padrão existentes */}
                    {filteredManuals.map(manual => (
                      <button
                        key={manual.id}
                        onClick={() => {
                          setSelectedManualType('standard');
                          setSelectedManual(manual);
                          setSelectedABNTManual(null);
                        }}
                        className={cn(
                          "w-full text-left rounded-lg px-3 py-2.5 transition-colors hover:bg-muted",
                          selectedManualType === 'standard' && selectedManual?.id === manual.id && "bg-muted ring-1 ring-primary"
                        )}
                      >
                        <p className="font-medium text-sm line-clamp-2">{manual.title}</p>
                        <div className="flex gap-2 mt-1.5 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{manual.category}</Badge>
                          {manual.systems && (
                            <Badge variant="outline" className="text-xs">{manual.systems.name}</Badge>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Coluna Central - Índice/TOC (200px) */}
              <Card className="flex flex-col h-full min-h-0">
                <CardHeader className="pb-3 border-b">
                  <CardTitle className="text-sm font-semibold">Índice</CardTitle>
                </CardHeader>
                <CardContent className="flex-1 min-h-0 overflow-y-auto p-3 custom-scrollbar">
                  {selectedManualType === 'abnt' && selectedABNTManual ? (
                    <ManualABNTTOC
                      sections={selectedABNTManual.body.sections}
                      activeSection={activeSection}
                    onSectionClick={(sectionNumber) => {
                      const element = document.getElementById(`section-${sectionNumber}`);
                      const container = document.getElementById('manual-abnt-inline-container');
                      if (container && element) {
                        const top = element.offsetTop - container.offsetTop - 16;
                        container.scrollTo({ top, behavior: 'smooth' });
                      }
                    }}
                    />
                  ) : selectedManualType === 'standard' && selectedManual && tocItems.length > 0 ? (
                    <ManualTOC
                      items={tocItems}
                      activeAnchor={activeAnchor}
                      onItemClick={(anchor) => {
                        setActiveAnchor(anchor);
                        const container = manualContentRef.current;
                        const element = document.getElementById(anchor);
                        
                        if (container && element) {
                          const scrollTop = element.offsetTop - container.offsetTop - 20;
                          container.scrollTo({
                            top: scrollTop,
                            behavior: 'smooth'
                          });
                        }
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-xs text-muted-foreground text-center">
                        Selecione um manual
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Coluna Direita - Conteúdo do Manual */}
              <Card className="flex flex-col h-full min-h-0">
                {selectedManualType === 'abnt' && selectedABNTManual ? (
                  <ManualABNTInline
                    manualData={selectedABNTManual}
                    onScrollSpy={setActiveSection}
                  />
                ) : selectedManualType === 'standard' && selectedManual ? (
                  <>
                    <CardHeader className="border-b pb-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{selectedManual.title}</CardTitle>
                          <CardDescription className="mt-1">
                            <div className="flex items-center gap-2 text-xs">
                              <Badge variant="secondary">{selectedManual.category}</Badge>
                              {selectedManual.systems && (
                                <Badge variant="outline">{selectedManual.systems.name}</Badge>
                              )}
                              {selectedManual.updated_at && (
                                <span className="text-muted-foreground">
                                  • Atualizado em {format(new Date(selectedManual.updated_at), "dd/MM/yyyy")}
                                </span>
                              )}
                            </div>
                          </CardDescription>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditingManual(selectedManual);
                              setShowManualModal(true);
                            }}>
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.print()}>
                              Imprimir
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => deleteManual.mutate(selectedManual.id)}
                              className="text-destructive"
                            >
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent ref={manualContentRef} id="manual-scroll-container" className="flex-1 min-h-0 overflow-y-auto p-6 custom-scrollbar">
                      <ManualContent
                        content={htmlToMarkdown(selectedManual.content)} 
                        manualId={selectedManual.id}
                        onScrollSpy={setActiveAnchor}
                      />
                    </CardContent>
                  </>
                ) : (
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Selecione um manual para visualizar
                      </p>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <FAQModal
        open={showFAQModal}
        onOpenChange={(open) => {
          setShowFAQModal(open);
          if (!open) setEditingFAQ(null);
        }}
        faq={editingFAQ}
      />

      <ManualModal
        open={showManualModal}
        onOpenChange={(open) => {
          setShowManualModal(open);
          if (!open) setEditingManual(null);
        }}
        manual={editingManual}
      />

      {selectedTicketId && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          open={showTicketDetail}
          onOpenChange={(open) => {
            setShowTicketDetail(open);
            if (!open) setSelectedTicketId(null);
          }}
        />
      )}
    </div>
  );
}